import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Mail, 
  Send, 
  TestTube, 
  Play, 
  Pause, 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Clock, 
  Eye, 
  Download, 
  Upload, 
  Settings, 
  BarChart3, 
  Users, 
  Target, 
  Zap,
  ExternalLink,
  Copy,
  Trash2,
  Plus,
  Filter,
  Search,
  Calendar,
  Activity,
  TrendingUp,
  FileText,
  Lightbulb,
  AlertCircle,
  CheckCheck,
  Timer,
  GitBranch,
  Brain,
  Sparkles,
  Monitor,
  Smartphone,
  Globe,
  Link,
  Database
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { format, formatDistanceToNow } from "date-fns";
import { 
  type CustomJourney as SharedCustomJourney,
  type JourneyStep,
  type CustomEmailTemplate,
  type JourneyCondition
} from "@shared/schema";

// Extended types for testing functionality
type CustomJourney = SharedCustomJourney & {
  steps?: JourneyStep[];
  templateName?: string;
  segmentName?: string;
  totalUsers?: number;
  estimatedDuration?: string;
};

type TestEmailRequest = {
  journeyId: number;
  stepId?: number;
  testEmails: string[];
  useRealData: boolean;
  userId?: number;
  variables?: Record<string, any>;
};

type JourneySimulation = {
  journeyId: number;
  simulationName: string;
  userCount: number;
  userAttributes: Record<string, any>[];
  startDate: string;
  speedMultiplier: number;
};

type TestResult = {
  id: number;
  type: 'email' | 'simulation' | 'validation';
  journeyId: number;
  journeyName: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  startedAt: string;
  completedAt?: string;
  results: any;
  createdBy: number;
};

type ABTestVariation = {
  id: number;
  name: string;
  templateId: number;
  trafficPercentage: number;
  isControl: boolean;
  metrics?: {
    opens: number;
    clicks: number;
    conversions: number;
  };
};

type ValidationResult = {
  type: 'link' | 'variable' | 'logic' | 'template';
  severity: 'error' | 'warning' | 'info';
  message: string;
  stepId?: number;
  suggestion?: string;
};

type LoggedEvent = {
  id: string;
  type: string;
  userId?: number | string;
  timestamp: string;
  receivedAt: string;
  data: Record<string, any>;
  metadata?: Record<string, any>;
};

type EventLogResponse = {
  events: LoggedEvent[];
  eventTypes: string[];
};

// Form schemas
const testEmailSchema = z.object({
  journeyId: z.number(),
  stepId: z.number().optional(),
  testEmails: z.array(z.string().email()).min(1, "At least one email address is required"),
  useRealData: z.boolean().default(false),
  userId: z.number().optional(),
  variables: z.record(z.any()).optional()
});

const simulationSchema = z.object({
  journeyId: z.number(),
  simulationName: z.string().min(1, "Simulation name is required"),
  userCount: z.number().min(1).max(10000),
  userProfile: z.string().default("random"),
  startDate: z.string().optional(),
  speedMultiplier: z.number().min(1).max(100).default(1)
});

const abTestSchema = z.object({
  journeyId: z.number(),
  testName: z.string().min(1, "Test name is required"),
  variations: z.array(z.object({
    name: z.string().min(1),
    templateId: z.number(),
    trafficPercentage: z.number().min(0).max(100)
  })).min(2, "At least 2 variations required"),
  duration: z.number().min(1).max(30).default(7),
  successMetric: z.string().default("conversions")
});

type TestEmailForm = z.infer<typeof testEmailSchema>;
type SimulationForm = z.infer<typeof simulationSchema>;
type ABTestForm = z.infer<typeof abTestSchema>;

interface JourneyTestingProps {
  journeyId?: number;
  onBack?: () => void;
}

// Sample data for variable substitution
const SAMPLE_USER_DATA = {
  firstName: 'Sarah',
  lastName: 'Johnson',
  email: 'sarah@example.com',
  tierPoints: '1,250',
  tier: 'Gold',
  competitionTitle: 'Weekly Challenge',
  prizeDescription: '£500 Cash Prize',
  siteName: 'Prize Drop'
};

export default function JourneyTesting({ journeyId, onBack }: JourneyTestingProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("email-testing");
  const [selectedJourney, setSelectedJourney] = useState<CustomJourney | null>(null);
  const [selectedTestResult, setSelectedTestResult] = useState<TestResult | null>(null);
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [isSimulationDialogOpen, setIsSimulationDialogOpen] = useState(false);
  const [isABTestDialogOpen, setIsABTestDialogOpen] = useState(false);
  const [isValidationRunning, setIsValidationRunning] = useState(false);
  const [simulationStatus, setSimulationStatus] = useState<'idle' | 'running' | 'paused' | 'completed'>('idle');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("all");

  // Fetch journeys for selection
  const { data: journeysData, isLoading: journeysLoading } = useQuery({
    queryKey: ['/api/admin/journey-builder/journeys'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/admin/journey-builder/journeys?limit=100');
      return response.json();
    }
  });

  // Fetch specific journey if journeyId is provided
  const { data: journeyData, isLoading: journeyLoading } = useQuery({
    queryKey: ['/api/admin/journey-builder/journeys', journeyId],
    queryFn: async () => {
      if (!journeyId) return null;
      const response = await apiRequest('GET', `/api/admin/journey-builder/journeys/${journeyId}`);
      return response.json();
    },
    enabled: !!journeyId
  });

  // Fetch test results
  const { data: testResults, isLoading: testResultsLoading, refetch: refetchTestResults } = useQuery({
    queryKey: ['/api/admin/journey-testing/results', selectedJourney?.id],
    queryFn: async () => {
      if (!selectedJourney?.id) return [];
      const response = await apiRequest('GET', `/api/admin/journey-testing/results?journeyId=${selectedJourney.id}`);
      return response.json();
    },
    enabled: !!selectedJourney?.id
  });

  const {
    data: eventLogData,
    isLoading: eventLogLoading,
    refetch: refetchEventLog,
  } = useQuery<EventLogResponse>({
    queryKey: ['/api/admin/journey-testing/events', eventTypeFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '200' });
      if (eventTypeFilter !== 'all') {
        params.set('type', eventTypeFilter);
      }
      const response = await apiRequest('GET', `/api/admin/journey-testing/events?${params.toString()}`);
      return response.json() as Promise<EventLogResponse>;
    },
    refetchInterval: 5000,
  });

  // Fetch user segments for simulation
  const { data: segments } = useQuery({
    queryKey: ['/api/segments'],
  });

  const journeys = journeysData?.journeys || [];
  const events = eventLogData?.events || [];
  const eventTypes = useMemo(() => {
    const types = eventLogData?.eventTypes || [];
    const unique = Array.from(new Set(types));
    return ['all', ...unique];
  }, [eventLogData?.eventTypes]);

  useEffect(() => {
    if (eventTypeFilter !== 'all' && !eventTypes.includes(eventTypeFilter)) {
      setEventTypeFilter('all');
    }
  }, [eventTypes, eventTypeFilter]);

  // Set selected journey from prop or first journey
  useEffect(() => {
    if (journeyId && journeyData) {
      setSelectedJourney(journeyData);
    } else if (!selectedJourney && journeys.length > 0) {
      setSelectedJourney(journeys[0]);
    }
  }, [journeyId, journeyData, journeys, selectedJourney]);

  // Form instances
  const testEmailForm = useForm<TestEmailForm>({
    resolver: zodResolver(testEmailSchema),
    defaultValues: {
      journeyId: selectedJourney?.id || 0,
      testEmails: [],
      useRealData: false,
      variables: SAMPLE_USER_DATA
    }
  });

  const simulationForm = useForm<SimulationForm>({
    resolver: zodResolver(simulationSchema),
    defaultValues: {
      journeyId: selectedJourney?.id || 0,
      simulationName: "",
      userCount: 100,
      userProfile: "random",
      speedMultiplier: 10
    }
  });

  const abTestForm = useForm<ABTestForm>({
    resolver: zodResolver(abTestSchema),
    defaultValues: {
      journeyId: selectedJourney?.id || 0,
      testName: "",
      variations: [],
      duration: 7,
      successMetric: "conversions"
    }
  });

  // Update forms when journey selection changes
  useEffect(() => {
    if (selectedJourney) {
      testEmailForm.setValue('journeyId', selectedJourney.id);
      simulationForm.setValue('journeyId', selectedJourney.id);
      abTestForm.setValue('journeyId', selectedJourney.id);
      simulationForm.setValue('simulationName', `Test ${selectedJourney.name} - ${new Date().toLocaleDateString()}`);
      abTestForm.setValue('testName', `A/B Test ${selectedJourney.name} - ${new Date().toLocaleDateString()}`);
    }
  }, [selectedJourney, testEmailForm, simulationForm, abTestForm]);

  // Send test email mutation
  const sendTestEmailMutation = useMutation({
    mutationFn: async (data: TestEmailForm) => {
      const response = await apiRequest('POST', '/api/admin/journey-testing/send-test-email', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    },
    onSuccess: (result) => {
      toast({ 
        title: "Test emails sent successfully!", 
        description: `Sent to ${result.emailCount} recipients`
      });
      setIsTestDialogOpen(false);
      testEmailForm.reset();
      refetchTestResults();
    },
    onError: (error: any) => {
      toast({
        title: "Error sending test emails",
        description: error.message || "Failed to send test emails",
        variant: "destructive"
      });
    }
  });

  // Run simulation mutation
  const runSimulationMutation = useMutation({
    mutationFn: async (data: SimulationForm) => {
      const response = await apiRequest('POST', '/api/admin/journey-testing/run-simulation', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    },
    onSuccess: (result) => {
      toast({ 
        title: "Journey simulation started!", 
        description: `Simulating ${result.userCount} users through the journey`
      });
      setIsSimulationDialogOpen(false);
      setSimulationStatus('running');
      simulationForm.reset();
      refetchTestResults();
    },
    onError: (error: any) => {
      toast({
        title: "Error starting simulation",
        description: error.message || "Failed to start simulation",
        variant: "destructive"
      });
    }
  });

  // Create A/B test mutation
  const createABTestMutation = useMutation({
    mutationFn: async (data: ABTestForm) => {
      const response = await apiRequest('POST', '/api/admin/journey-testing/create-ab-test', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    },
    onSuccess: (result) => {
      toast({ 
        title: "A/B test created successfully!", 
        description: `Test will run for ${result.duration} days`
      });
      setIsABTestDialogOpen(false);
      abTestForm.reset();
      refetchTestResults();
    },
    onError: (error: any) => {
      toast({
        title: "Error creating A/B test",
        description: error.message || "Failed to create A/B test",
        variant: "destructive"
      });
    }
  });

  // Validate journey mutation
  const validateJourneyMutation = useMutation({
    mutationFn: async (journeyId: number) => {
      const response = await apiRequest('POST', `/api/admin/journey-testing/validate/${journeyId}`);
      return response.json();
    },
    onSuccess: (result) => {
      toast({ 
        title: "Journey validation completed", 
        description: `Found ${result.issues.length} issues`
      });
      setIsValidationRunning(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error validating journey",
        description: error.message || "Failed to validate journey",
        variant: "destructive"
      });
      setIsValidationRunning(false);
    }
  });

  const clearEventLogMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('DELETE', '/api/admin/journey-testing/events');
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Event history cleared",
        description: "Event log has been reset for the current session."
      });
      refetchEventLog();
    },
    onError: (error: any) => {
      toast({
        title: "Unable to clear event log",
        description: error.message || "Failed to clear event history",
        variant: "destructive"
      });
    }
  });

  const handleSendTestEmail = (data: TestEmailForm) => {
    sendTestEmailMutation.mutate(data);
  };

  const handleRunSimulation = (data: SimulationForm) => {
    runSimulationMutation.mutate(data);
  };

  const handleCreateABTest = (data: ABTestForm) => {
    createABTestMutation.mutate(data);
  };

  const handleValidateJourney = () => {
    if (!selectedJourney) return;
    setIsValidationRunning(true);
    validateJourneyMutation.mutate(selectedJourney.id);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      'completed': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'failed': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'running': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'pending': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    };
    
    return (
      <Badge className={variants[status as keyof typeof variants] || variants.pending}>
        {status}
      </Badge>
    );
  };

  if (journeyLoading || journeysLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-600">Loading journey testing...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="journey-testing-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Journey Testing</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Test and validate your journey flows before launching campaigns
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack} data-testid="button-back">
            ← Back to Builder
          </Button>
        )}
      </div>

      {/* Journey Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Select Journey
          </CardTitle>
          <CardDescription>
            Choose the journey you want to test and validate
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="journey-select">Journey</Label>
              <Select
                value={selectedJourney?.id.toString() || ""}
                onValueChange={(value) => {
                  const journey = journeys.find(j => j.id === parseInt(value));
                  setSelectedJourney(journey || null);
                }}
                data-testid="select-journey"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a journey to test" />
                </SelectTrigger>
                <SelectContent>
                  {journeys.map((journey) => (
                    <SelectItem key={journey.id} value={journey.id.toString()}>
                      <div className="flex items-center gap-2">
                        <span>{journey.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {journey.status}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedJourney && (
              <div className="space-y-2">
                <Label>Journey Details</Label>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <div><strong>Type:</strong> {selectedJourney.journeyType}</div>
                  <div><strong>Trigger:</strong> {selectedJourney.triggerEvent || 'Not configured'}</div>
                  <div><strong>SES Template:</strong> {selectedJourney.sesTemplateName || 'Not set'}</div>
                  <div className="flex items-center gap-2"><strong>Status:</strong> {getStatusBadge(selectedJourney.status || 'draft')}</div>
                </div>
              </div>
            )}
          </div>
      </CardContent>
    </Card>

      <EventLogPanel
        events={events}
        isLoading={eventLogLoading}
        onRefresh={() => {
          void refetchEventLog();
        }}
        onClear={() => clearEventLogMutation.mutate()}
        isClearing={clearEventLogMutation.isPending}
        eventTypes={eventTypes}
        currentFilter={eventTypeFilter}
        onFilterChange={setEventTypeFilter}
      />

      {selectedJourney ? (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="email-testing" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email Testing
            </TabsTrigger>
            <TabsTrigger value="simulation" className="flex items-center gap-2">
              <TestTube className="w-4 h-4" />
              Simulation
            </TabsTrigger>
            <TabsTrigger value="ab-testing" className="flex items-center gap-2">
              <GitBranch className="w-4 h-4" />
              A/B Testing
            </TabsTrigger>
            <TabsTrigger value="validation" className="flex items-center gap-2">
              <CheckCheck className="w-4 h-4" />
              Validation
            </TabsTrigger>
            <TabsTrigger value="event-log" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Event Log
            </TabsTrigger>
            <TabsTrigger value="results" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Results
            </TabsTrigger>
          </TabsList>

          {/* Email Testing Tab */}
          <TabsContent value="email-testing" className="space-y-4">
            <EmailTestingPanel 
              journey={selectedJourney}
              onSendTest={handleSendTestEmail}
              testEmailForm={testEmailForm}
              isLoading={sendTestEmailMutation.isPending}
            />
          </TabsContent>

          {/* Simulation Tab */}
          <TabsContent value="simulation" className="space-y-4">
            <SimulationPanel
              journey={selectedJourney}
              onRunSimulation={handleRunSimulation}
              simulationForm={simulationForm}
              simulationStatus={simulationStatus}
              isLoading={runSimulationMutation.isPending}
            />
          </TabsContent>

          {/* A/B Testing Tab */}
          <TabsContent value="ab-testing" className="space-y-4">
            <ABTestingPanel
              journey={selectedJourney}
              onCreateTest={handleCreateABTest}
              abTestForm={abTestForm}
              isLoading={createABTestMutation.isPending}
            />
          </TabsContent>

          {/* Validation Tab */}
          <TabsContent value="validation" className="space-y-4">
            <ValidationPanel
              journey={selectedJourney}
              onValidate={handleValidateJourney}
              isValidating={isValidationRunning}
              validationResults={validateJourneyMutation.data?.issues || []}
            />
          </TabsContent>

          <TabsContent value="event-log" className="space-y-4">
            <EventLogPanel
              events={events}
              isLoading={eventLogLoading}
              onRefresh={() => {
                void refetchEventLog();
              }}
              onClear={() => clearEventLogMutation.mutate()}
              isClearing={clearEventLogMutation.isPending}
              eventTypes={eventTypes}
              currentFilter={eventTypeFilter}
              onFilterChange={setEventTypeFilter}
            />
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results" className="space-y-4">
            <TestResultsPanel
              journey={selectedJourney}
              results={testResults || []}
              isLoading={testResultsLoading}
              onRefresh={refetchTestResults}
            />
          </TabsContent>
        </Tabs>
      ) : null}
    </div>
  );
}

// Email Testing Panel Component
interface EmailTestingPanelProps {
  journey: CustomJourney;
  onSendTest: (data: TestEmailForm) => void;
  testEmailForm: any;
  isLoading: boolean;
}

function EmailTestingPanel({ journey, onSendTest, testEmailForm, isLoading }: EmailTestingPanelProps) {
  const [emailInput, setEmailInput] = useState("");
  const [testEmails, setTestEmails] = useState<string[]>([]);

  const addEmail = () => {
    if (emailInput && emailInput.includes("@") && !testEmails.includes(emailInput)) {
      setTestEmails([...testEmails, emailInput]);
      testEmailForm.setValue('testEmails', [...testEmails, emailInput]);
      setEmailInput("");
    }
  };

  const removeEmail = (email: string) => {
    const newEmails = testEmails.filter(e => e !== email);
    setTestEmails(newEmails);
    testEmailForm.setValue('testEmails', newEmails);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Test Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Send Test Emails
          </CardTitle>
          <CardDescription>
            Send test emails to specific addresses with real or sample data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...testEmailForm}>
            <form onSubmit={testEmailForm.handleSubmit(onSendTest)} className="space-y-4">
              {/* Email Recipients */}
              <div className="space-y-2">
                <Label>Test Email Recipients</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
                    data-testid="input-test-email"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={addEmail}
                    data-testid="button-add-email"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {testEmails.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {testEmails.map((email) => (
                      <Badge key={email} variant="secondary" className="flex items-center gap-1">
                        {email}
                        <button
                          type="button"
                          onClick={() => removeEmail(email)}
                          className="ml-1 hover:text-red-500"
                          data-testid={`button-remove-email-${email}`}
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Data Options */}
              <FormField
                control={testEmailForm.control}
                name="useRealData"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>Use Real User Data</FormLabel>
                      <FormDescription>
                        Use actual user data instead of sample data for variable substitution
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="switch-use-real-data"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* User Selection for Real Data */}
              {testEmailForm.watch('useRealData') && (
                <FormField
                  control={testEmailForm.control}
                  name="userId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Select User</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Enter User ID"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                          data-testid="input-user-id"
                        />
                      </FormControl>
                      <FormDescription>
                        Enter the ID of the user whose data should be used for testing
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <Button 
                type="submit" 
                disabled={isLoading || testEmails.length === 0}
                className="w-full"
                data-testid="button-send-test-emails"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending Test Emails...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Test Emails ({testEmails.length})
                  </>
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Email Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Email Preview
          </CardTitle>
          <CardDescription>
            Preview how emails will appear with sample data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Device Preview Tabs */}
            <Tabs defaultValue="desktop" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="desktop" className="flex items-center gap-2">
                  <Monitor className="w-4 h-4" />
                  Desktop
                </TabsTrigger>
                <TabsTrigger value="mobile" className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Mobile
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="desktop" className="mt-4">
                <div className="border rounded-lg p-4 bg-white dark:bg-gray-900 min-h-[300px]">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <strong>Subject:</strong> Welcome to {SAMPLE_USER_DATA.siteName}, {SAMPLE_USER_DATA.firstName}!
                  </div>
                  <div className="prose prose-sm max-w-none">
                    <h2>Hi {SAMPLE_USER_DATA.firstName}!</h2>
                    <p>Welcome to {SAMPLE_USER_DATA.siteName}! We're excited to have you join our community.</p>
                    <p>Your current tier: <strong>{SAMPLE_USER_DATA.tier}</strong> ({SAMPLE_USER_DATA.tierPoints} points)</p>
                    <p>Don't forget to check out our latest competition: <strong>{SAMPLE_USER_DATA.competitionTitle}</strong></p>
                    <Button className="mt-4">Get Started</Button>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="mobile" className="mt-4">
                <div className="flex justify-center">
                  <div className="w-80 bg-gray-900 rounded-3xl p-2">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden">
                      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b">
                        <div className="text-sm font-medium truncate">
                          Welcome to {SAMPLE_USER_DATA.siteName}, {SAMPLE_USER_DATA.firstName}!
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {SAMPLE_USER_DATA.siteName} &lt;noreply@prizedrop.com&gt;
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="text-sm">
                          <p><strong>Hi {SAMPLE_USER_DATA.firstName}!</strong></p>
                          <p className="mt-2">Welcome to {SAMPLE_USER_DATA.siteName}!</p>
                          <p className="mt-2">Tier: <strong>{SAMPLE_USER_DATA.tier}</strong></p>
                          <Button size="sm" className="mt-4 w-full">Get Started</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Variable Information */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Available Variables</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(SAMPLE_USER_DATA).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{`{{${key}}}`}</span>
                    <span className="font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Simulation Panel Component (placeholder structure)
interface SimulationPanelProps {
  journey: CustomJourney;
  onRunSimulation: (data: SimulationForm) => void;
  simulationForm: any;
  simulationStatus: string;
  isLoading: boolean;
}

function SimulationPanel({ journey, onRunSimulation, simulationForm, simulationStatus, isLoading }: SimulationPanelProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TestTube className="w-5 h-5" />
          Journey Simulation
        </CardTitle>
        <CardDescription>
          Simulate user flows through your journey without sending emails
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-center py-8 text-gray-500">
          <TestTube className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Simulation features coming soon...</p>
          <p className="text-sm">Test user journeys with virtual users and see how they progress through your flows.</p>
        </div>
      </CardContent>
    </Card>
  );
}

// A/B Testing Panel Component (placeholder structure)
interface ABTestingPanelProps {
  journey: CustomJourney;
  onCreateTest: (data: ABTestForm) => void;
  abTestForm: any;
  isLoading: boolean;
}

function ABTestingPanel({ journey, onCreateTest, abTestForm, isLoading }: ABTestingPanelProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="w-5 h-5" />
          A/B Testing
        </CardTitle>
        <CardDescription>
          Create and manage A/B tests for your journey variations
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-center py-8 text-gray-500">
          <GitBranch className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>A/B testing features coming soon...</p>
          <p className="text-sm">Test multiple variations of your journeys to optimize performance.</p>
        </div>
      </CardContent>
    </Card>
  );
}

// Validation Panel Component (placeholder structure)
interface ValidationPanelProps {
  journey: CustomJourney;
  onValidate: () => void;
  isValidating: boolean;
  validationResults: ValidationResult[];
}

function ValidationPanel({ journey, onValidate, isValidating, validationResults }: ValidationPanelProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCheck className="w-5 h-5" />
          Journey Validation
        </CardTitle>
        <CardDescription>
          Validate your journey for common issues and optimization opportunities
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-center py-8 text-gray-500">
          <CheckCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Validation features coming soon...</p>
          <p className="text-sm">Automatically check for broken links, logic errors, and optimization opportunities.</p>
        </div>
      </CardContent>
    </Card>
  );
}

interface EventLogPanelProps {
  events: LoggedEvent[];
  isLoading: boolean;
  onRefresh: () => void | Promise<unknown>;
  onClear: () => void | Promise<unknown>;
  isClearing: boolean;
  eventTypes: string[];
  currentFilter: string;
  onFilterChange: (value: string) => void;
}

function EventLogPanel({
  events,
  isLoading,
  onRefresh,
  onClear,
  isClearing,
  eventTypes,
  currentFilter,
  onFilterChange,
}: EventLogPanelProps) {
  return (
    <Card data-testid="card-event-log">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Emitted Events
        </CardTitle>
        <CardDescription>
          Real-time CRM events captured during testing. The log persists in memory until cleared or the server restarts.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Label htmlFor="event-type-filter" className="text-sm font-medium">
              Event Type
            </Label>
            <Select value={currentFilter} onValueChange={onFilterChange}>
              <SelectTrigger id="event-type-filter" className="w-48">
                <SelectValue placeholder="All events" />
              </SelectTrigger>
              <SelectContent>
                {eventTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type === 'all' ? 'All events' : type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={isLoading}
              data-testid="button-refresh-event-log"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onClear}
              disabled={isClearing || events.length === 0}
              data-testid="button-clear-event-log"
            >
              <Trash2 className={`w-4 h-4 mr-2 ${isClearing ? 'animate-spin' : ''}`} />
              Clear Log
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
            Loading event history...
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No events recorded yet.</p>
            <p className="text-sm">
              Trigger a test journey or wait for live activity (for example, new user registrations) to see events here.
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[360px] pr-4">
            <div className="space-y-3">
              {events.map((event) => {
                const emittedDate = new Date(event.timestamp);
                const emittedTime = format(emittedDate, 'MMM d, HH:mm:ss');
                const relativeTime = formatDistanceToNow(emittedDate, { addSuffix: true });

                return (
                  <div
                    key={event.id}
                    className="border border-border/60 rounded-lg bg-muted/40 p-4 space-y-3"
                    data-testid={`event-log-item-${event.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="uppercase tracking-wide text-xs">
                          {event.type}
                        </Badge>
                        {event.userId && (
                          <span className="text-xs text-muted-foreground">
                            User: {event.userId}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground text-right">
                        {emittedTime} · {relativeTime}
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <div className="text-xs text-muted-foreground">
                        <strong>Emitted:</strong> {emittedTime} — <strong>Received:</strong>{' '}
                        {format(new Date(event.receivedAt), 'HH:mm:ss.SSS')}
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Payload</Label>
                        <pre className="mt-1 bg-background border rounded p-3 text-xs overflow-x-auto">
                          {JSON.stringify(event.data, null, 2)}
                        </pre>
                      </div>
                      {event.metadata && Object.keys(event.metadata).length > 0 && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-muted-foreground">View metadata</summary>
                          <pre className="mt-2 bg-background border rounded p-3 text-xs overflow-x-auto">
                            {JSON.stringify(event.metadata, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}

// Test Results Panel Component (placeholder structure)
interface TestResultsPanelProps {
  journey: CustomJourney;
  results: TestResult[];
  isLoading: boolean;
  onRefresh: () => void;
}

function TestResultsPanel({ journey, results, isLoading, onRefresh }: TestResultsPanelProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Test Results
        </CardTitle>
        <CardDescription>
          View results from your testing activities
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-center py-8 text-gray-500">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Test results dashboard coming soon...</p>
          <p className="text-sm">Track and analyze the results of your tests and simulations.</p>
        </div>
      </CardContent>
    </Card>
  );
}
